import React, { useState } from "react";
import {
  Spinner,
  Text,
  Card,
  Button,
  Link,
  TabList,
  Tab,
  Badge,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  Mail24Regular,
  Person24Regular,
  Briefcase24Regular,
  Add24Regular,
  ArrowSync24Regular,
  QuestionCircle20Regular,
} from "@fluentui/react-icons";
import { useCrmLookup } from "../../hooks/useCrmLookup";
import { useOutlookItem } from "../../hooks/useOutlookItem";
import { ContactCard } from "./ContactCard";
import { OpportunityList } from "./OpportunityList";
import { FileEmailButton } from "./FileEmailButton";
import { CreateRecordForm } from "./CreateRecordForm";
import { CreateLeadForm } from "./CreateLeadForm";

const useStyles = makeStyles({
  root: { padding: "12px" },
  senderCard: { marginBottom: "12px", padding: "8px" },
  tabContent: { marginTop: "12px" },
  helpLink: {
    position: "fixed" as const,
    bottom: "12px",
    right: "12px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    color: tokens.colorNeutralForeground3,
  },
});

export const ContactPanel: React.FC = () => {
  const { currentItem, loading: itemLoading } = useOutlookItem();
  const { contacts, leads, opportunities, loading: crmLoading, error, refresh } =
    useCrmLookup(currentItem?.senderEmail);

  const [activeTab, setActiveTab] = useState("overview");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showCreateLead, setShowCreateLead] = useState(false);
  const styles = useStyles();

  if (itemLoading || crmLoading) {
    return <Spinner label="Looking up sender in CRM..." />;
  }

  if (error) {
    return (
      <Card style={{ margin: "12px", padding: "12px" }}>
        <Text>Error: {error.message}</Text>
        <Button icon={<ArrowSync24Regular />} onClick={refresh} style={{ marginTop: "8px" }}>
          Retry
        </Button>
      </Card>
    );
  }

  const hasRecords = contacts.length > 0 || leads.length > 0;

  return (
    <div className={styles.root}>
      <Card className={styles.senderCard}>
        <Text weight="semibold" size={400}>{currentItem?.senderName || "No email selected"}</Text>
        {currentItem?.senderEmail && <Text size={200} block>{currentItem.senderEmail}</Text>}
        {currentItem?.subject && <Text size={200} italic block>{currentItem.subject}</Text>}
      </Card>

      <TabList
        selectedValue={activeTab}
        onTabSelect={(_, data) => setActiveTab(data.value as string)}
        size="small"
        style={{ justifyContent: "center" }}
      >
        <Tab value="overview">Overview</Tab>
        <Tab value="opportunities">
          Opps
          {opportunities.length > 0 && (
            <Badge appearance="filled" color="brand" size="small" style={{ marginLeft: "4px" }}>
              {opportunities.length}
            </Badge>
          )}
        </Tab>
        <Tab value="actions">Actions</Tab>
      </TabList>

      <div className={styles.tabContent}>
        {activeTab === "overview" && (
          <div>
            {hasRecords ? (
              <>
                {contacts.map((c) => <ContactCard key={c.contactid} record={c} type="contact" />)}
                {leads.map((l) => <ContactCard key={l.leadid} record={l} type="lead" />)}
              </>
            ) : (
              <Card style={{ padding: "12px" }}>
                <Text>No matching records found in CRM.</Text>
                <Button
                  icon={<Add24Regular />}
                  onClick={() => setShowCreateForm(true)}
                  style={{ marginTop: "8px" }}
                >
                  Create New Record
                </Button>
              </Card>
            )}
            <div className={styles.helpLink}>
              <QuestionCircle20Regular />
              <Link href={`${window.location.origin}/user-guide-source.html`} target="_blank" size="small">
                Help
              </Link>
            </div>
          </div>
        )}

        {activeTab === "opportunities" && (
          <OpportunityList opportunities={opportunities} />
        )}

        {activeTab === "actions" && currentItem && (
          <>
            <FileEmailButton mailItem={currentItem} contacts={contacts} leads={leads} />
            {!showCreateLead && (
              <Button
                icon={<Add24Regular />}
                appearance="outline"
                onClick={() => setShowCreateLead(true)}
                style={{ marginTop: "8px" }}
              >
                Create Lead
              </Button>
            )}
            {showCreateLead && (
              <CreateLeadForm
                prefillEmail={currentItem?.senderEmail}
                prefillName={currentItem?.senderName}
                onClose={() => setShowCreateLead(false)}
                onCreated={() => { setShowCreateLead(false); refresh(); }}
              />
            )}
          </>
        )}

        {showCreateForm && (
          <CreateRecordForm
            prefillEmail={currentItem?.senderEmail}
            prefillName={currentItem?.senderName}
            onClose={() => setShowCreateForm(false)}
            onCreated={() => { setShowCreateForm(false); refresh(); }}
          />
        )}
      </div>
    </div>
  );
};
